import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, Image, Modal, SafeAreaView, ScrollView } from 'react-native';
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useIsFocused } from '@react-navigation/native';
import DropDownPicker from 'react-native-dropdown-picker';
import { DataStore, Predicates, SortDirection } from 'aws-amplify';
import { getStorage, saveStorage } from '../components/SessionStorage';
import { AppUser, Farm, DailyLog, DayDiagnosis, UserGroup, Category, UserType, CultivationEvent, EventCategories, Varieties } from '../../models';
import { formatToDate, formatDate, formatDateAndWeek, toLocalString } from '../components/CmnUtil';
import IconImages from '../components/IconImages';
import * as CmnUtil from '../components/CmnUtil';
import { Header } from "../components/Header";
import DatePicker from "react-datepicker";
import ja from "date-fns/locale/ja";
import { Checkbox } from "react-native-paper";

/** ================================
*  作業日誌トップ画面
*  ================================*/
const DiaryTop = ({ navigation }: { navigation: any; }) => {
  // 画面遷移引数
  const [prodGroupId, setProdGroupId] = useState("");
  const [coopGroupName, setCoopGroupName] = useState("");

  // 選択日付
  const [dispDate, setDispDate] = useState(new Date());

  // カレンダー日付
  const [selectYear, setSelectYear] = useState(0);
  const [selectMonth, setSelectMonth] = useState(0);
  const [selectDate, setSelectDate] = useState(0);

  // 現在日
  const [currentDate, setCurrentDate] = useState(new Date());

  // 圃場一覧
  const [farmList, setFarmList] = useState(Array());

  // 設定圃場ID
  const [farm, setFarm] = useState<Farm>();

  // 作業日誌一覧
  const [diaryList, setDiaryList] = useState(Array());

  // 選択日誌表示一覧
  const [selectDiaryList, setDispDiaryList] = useState(Array());

  // 選択種別
  const [selectKind, setSelectKind] = useState("");

  // 選択日誌
  const [selectDiary, setSelectDiary] = useState<any>();

  // 診断結果
  const [selectDayDignosis, setSelectDayDignosis] = useState(Array());

  // イベント
  const [selectDayEvent, setSelectDayEvent] = useState(Array());

  // 圃場選択ドロップダウン
  const [open, setOpen] = React.useState(false);
  const [pickerValue, setPickerValue] = React.useState("");
  const [pickerItems, setPickerItems] = React.useState([{ label: "default", value: "default" }]);
  const [pickerZIndex, setPickerZIndex] = React.useState(1000);

  // 作業日誌詳細モーダル表示State
  const [ismodal, setIsModal] = useState<boolean>(false);

  // カレンダー年月選択Picker
  const [isOpenCalender, setIsOpenCalender] = useState<boolean>(false);

  // 品種絞り込み
  const [conditionDisp, setConditionDisp] = useState<boolean>(false);
  const [condition, setCondition] = useState<{ checked: boolean, value: Varieties }[]>([]);

  // フォーカス
  const isFocused = useIsFocused();

  // カレンダー表示数
  const dispCalenderNum = window.innerHeight > 750 ? 3 : 2;

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    // 画面フォーカス時に再描画
    if (isFocused && (selectYear != 0)) {
      checkFriendProducer().then((result) => {
        if (result) {
          reloadFarmList();
        } else {
          init();
        }
      });
    }
  }, [isFocused, prodGroupId]);

  useEffect(() => {
    if (farm) {
      getDiaryLog();
    }
  }, [selectMonth, selectYear, farm]);

  useEffect(() => {
    getDiaryLog();
  }, [condition]);

  /**
   * 初期設定
   */
  const init = async () => {
    // 日付設定
    const date = new Date();
    setSelectYear(date.getFullYear());
    setSelectMonth(date.getMonth());
    setSelectDate(date.getDate());
    await saveStorage('diaryDate', formatDate(new Date()));

    // ログインユーザの取得
    const authUser = await getStorage('authUser');
    let group_id = authUser.groupId;

    // 普及指導員の場合は、表示対象の生産者を取得
    if (authUser.userType === UserType.INSTRUCTOR) {
      group_id = await getStorage('friendProducer');

      setProdGroupId(group_id);
      await DataStore.query(UserGroup, c => c.id("eq", group_id))
        .then((user) => {
          setCoopGroupName(user[0].groupName);
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // 圃場データの取得
    const farms = (await DataStore.query(Farm, Predicates.ALL, {
      sort: s => s.name(SortDirection.ASCENDING)
    })).filter(c => c.group.id === group_id);

    // セッションストレージに選択圃場情報を保存
    await saveStorage('farmList', farms);

    // 圃場選択リストの作成
    let farm_list = [{ label: "圃場が登録されていません", value: "default" }];
    farms.map((item: any, index: number) => {
      farm_list[index] = { label: item.name, value: item.name };
    });

    // 選択圃場の保存
    let result = farms.filter(function (value: any) {
      // 現在選択している圃場に一致するデータをセット
      return value.name === farm_list[0]["value"];
    });

    // 圃場一覧の保持
    setFarmList(farms);

    // 圃場リストの設定
    setPickerItems(farm_list);

    // 圃場選択リストの設定
    setPickerValue(farm_list[0]["value"]);

    const tmpVarieties: any = [];
    if (result[0].varieties) {
      for (const variety of result[0].varieties) {
        if (variety) {
          tmpVarieties.push({ checked: false, value: variety });
        }
      }
    }
    setCondition(tmpVarieties);
    setFarm(result[0]);
  };

  /**
   * 圃場再読み込み
   */
  const reloadFarmList = async () => {
    // ログインユーザの取得
    const authUser = await getStorage('authUser');
    let group_id = authUser.groupId;

    // 普及指導員の場合は、表示対象の生産者を取得
    if (authUser.userType === UserType.INSTRUCTOR) {
      group_id = await getStorage('friendProducer');

      setProdGroupId(group_id);
      await DataStore.query(UserGroup, c => c.id("eq", group_id))
        .then((user) => {
          setCoopGroupName(user[0].groupName);
        })
        .catch((error) => {
          console.error(error);
        });
    }

    // 圃場データの取得
    const farms = (await DataStore.query(Farm, Predicates.ALL, {
      sort: s => s.name(SortDirection.ASCENDING)
    })).filter(c => c.group.id === group_id);

    // セッションストレージに選択圃場情報を保存
    await saveStorage('farmList', farms);

    // 圃場選択リストの作成
    let farm_list = [{ label: "圃場が登録されていません", value: "default" }];
    farms.map((item: any, index: number) => {
      farm_list[index] = { label: item.name, value: item.name };
    });

    // 選択圃場の保存
    let result = farms.filter(function (value: any) {
      // 現在選択している圃場に一致するデータをセット
      return value.name === farm?.name;
    });
    if (result.length == 0) {
      result = farms.filter(function (value: any) {
        // 現在選択している圃場に一致するデータをセット
        return value.name === farm_list[0]["value"];
      });
    }

    // 圃場一覧の保持
    setFarmList(farms);

    // 圃場リストの設定
    setPickerItems(farm_list);

    // 圃場選択リストの設定
    let index = 0;
    for (let i = 0; i < farm_list.length; i++) {
      if (farm_list[i].value === farm?.name) {
        index = i;
        break;
      }
    }
    setPickerValue(farm_list[index]["value"]);

    const tmpVarieties: any = [];
    if (result[0].varieties) {
      for (const variety of result[0].varieties) {
        if (variety) {
          tmpVarieties.push({ checked: false, value: variety });
        }
      }
    }
    setCondition(tmpVarieties);
    setFarm(result[0]);
  };

  /**
   * 友だち切替チェック
   */
  const checkFriendProducer = async () => {
    let group_id = "";
    await getStorage('friendProducer').then((result) => {
      group_id = result === undefined ? "" : result;
    });
    return group_id === prodGroupId;
  };

  /** 
   * 圃場切り替え 
   */
  const changeFarm = () => {
    if (pickerValue) {
      let result = farmList.filter(function (value) {
        // 現在選択している圃場に一致するデータをセット
        return value.name === pickerValue;
      });

      const tmpVarieties: any = [];
      if (result[0].varieties) {
        for (const variety of result[0].varieties) {
          if (variety) {
            tmpVarieties.push({ checked: false, value: variety });
          }
        }
      }
      setCondition(tmpVarieties);
      setFarm(result[0]);
    }
  };

  /** 
   * 作業日誌取得 
   */
  const getDiaryLog = async () => {

    // 初期表示日付取得
    const diaryDate = await getStorage('diaryDate');
    const current_date = diaryDate ? formatToDate(diaryDate) : new Date(selectYear, selectMonth, selectDate);

    // カレンダー配列に作業日誌・診断データを追加する
    const calendar_date = new Date(selectYear, selectMonth, selectDate);

    const yyyy = calendar_date.getFullYear();
    const MM = ('0' + (calendar_date.getMonth() + 1)).slice(-2);
    const calendar_month = yyyy + '-' + MM;

    // 日誌データ取得
    let diary_list_pre;
    if (isNarrowed()) {
      diary_list_pre = (await DataStore.query(DailyLog)).filter(
        c => c.farm.id === farm?.id
          && c.date.match(calendar_month)
          && matchVariety(c.varieties)
      );
    } else {
      diary_list_pre = (await DataStore.query(DailyLog)).filter(
        c => c.farm.id === farm?.id
          && c.date.match(calendar_month)
      );
    }
    const diary_list = await Promise.all(diary_list_pre.map(async (diary) => {
      const user = await DataStore.query(AppUser, diary.authorID);
      return { ...diary, "authorName": user?.username };
    }));

    // 診断結果取得
    const dayDignosis = (await DataStore.query(DayDiagnosis)).filter(
      c => c.farm.id === farm?.id
        && c.isAggregated === true
        && toLocalString(c.date).match(calendar_month)
    );

    // イベントデータ取得
    let event_list_pre;
    if (isNarrowed()) {
      event_list_pre = (await DataStore.query(CultivationEvent)).filter(
        c => c.farm.id === farm?.id
          && c.startDate <= calendar_month + "-31"
          && calendar_month + "-01" <= c.finishDate
          && matchVariety(c.varieties)
      );
    } else {
      event_list_pre = (await DataStore.query(CultivationEvent)).filter(
        c => c.farm.id === farm?.id
          && c.startDate <= calendar_month + "-31"
          && calendar_month + "-01" <= c.finishDate
      );
    }

    const event_list = await Promise.all(event_list_pre.map(async (diary) => {
      const user = await DataStore.query(AppUser, diary.authorID);
      return { ...diary, "authorName": user?.username };
    }));

    let calendar_array = setDays(calendar_date);
    // 週ごとに処理開始
    calendar_array.map((calendar_rows) => {
      // 日ごとに処理開始
      calendar_rows.map((days) => {
        const diary_array = new Array();
        // 当月内の日付の場合
        if (days[1] !== "otherday") {
          // カレンダー日付に一致する日誌データを挿入する
          diary_list.map((diary) => {
            if (days[2] === diary.date) {
              diary_array.push(diary);
            }
          });
          days[3] = diary_array;

          // カレンダー日付に一致する診断データを挿入する
          const dignosis_array = new Array();
          dayDignosis.map((dignosis) => {
            // days[2]: YYYY-MM-DD
            if (days[2] === formatDate(new Date(dignosis.date))) {
              dignosis_array.push(dignosis);
            };
            days[4] = dignosis_array;
          });

          // カレンダー日付に一致するイベントデータを挿入する
          const event_array = new Array();
          event_list.map((event) => {
            // days[2]: YYYY-MM-DD
            if (event.startDate <= days[2] && days[2] <= event.finishDate) {
              event_array.push(event);
            };
          });
          days[5] = event_array;
        }
      });

      // 現在選択している日付に一致するカレンダーデータをセット
      let result = calendar_rows.filter(function (value: any, index: any) {
        return value[2] === formatDate(current_date);
      });

      // 作業日誌表示設定
      dispDiaryList(result[0]);
    });

    // 日誌データの保存
    setDiaryList(calendar_array);
  };

  /** 
   * 栽培品種が絞り込み条件に一致しているか判断する
   */
  const matchVariety = (value: (Varieties | null)[] | keyof typeof Varieties | null | undefined) => {
    if (value) {
      for (const variety of value) {
        if (variety) {
          for (const key of condition) {
            if (key.checked && key.value == variety) {
              return true;
            }
          }
        }
      }
    }
    return false;
  }

  /** 
   * 栽培品種が絞り込まれているか判断する
   */
  const isNarrowed = () => {
    for (const key of condition) {
      if (key.checked) {
        return true;
      }
    }
    return false;
  }

  /** 
   * 選択日の作業日誌・診断データの表示
   */
  const dispDiaryList = async (item: any) => {
    if (item !== '' && item !== undefined) {
      const selectedDate = formatToDate(item[2]);
      setDispDate(selectedDate);
      setDispDiaryList(item[3]);
      setSelectDayDignosis(item[4]);
      setSelectDayEvent(item[5]);
      await saveStorage('diaryDate', item[2]);
    }
  };

  /** 
   * 作業日誌詳細モーダル表示
   */
  const dispDiayDetail = (diary: DailyLog, kind: string) => {
    setSelectKind(kind);
    setSelectDiary(diary);
    setIsModal(!ismodal);
  };

  /** 
   * 作業日誌編集画面へ遷移
   */
  const goDiayEdit = (type: string, data: any = undefined, kind: any = undefined) => {
    navigation.navigate("DiaryEdit", {
      "farm": farmList,
      "kind": kind,
      "diary": data,
      "type": type,
    });
  };

  /** 
   * レポート画面へ遷移
   */
  const goReport = () => {
    navigation.navigate("DiaryReport", {
      "farm": farmList
    });
  };

  /**
   * 撮影画像一覧へ遷移
   */
  const goDiagnosisPhotoList = () => {
    navigation.navigate("DiagnosisNavigator", {
      screen: "DiagnosisPhotoList",
      initial: false,
      params: {
        'id': selectDayDignosis[0].id,
      }
    });
  };

  /**
   * ホームボタン
   */
  const onHome = () => {
    setProdGroupId("");
    setCoopGroupName("");
  };

  /** 
   * 前月のカレンダーへ 
   */
  const prevMonth = () => {
    // 年月の更新
    if (selectMonth == 0) {
      setSelectMonth(11);
      setSelectYear(selectYear - 1);
    } else {
      setSelectMonth(selectMonth - 1);
    }

    // 日付の更新
    // 当月の場合、今日の日付をセットする
    if (selectMonth - 1 == currentDate.getMonth()) {
      setSelectDate(currentDate.getDate());
    } else {
      setSelectDate(1);
    }
  };

  /** 
   * 次月のカレンダーへ 
   */
  const nextMonth = () => {
    // 年月の更新
    if (selectMonth == 11) {
      setSelectMonth(0);
      setSelectYear(selectYear + 1);
    } else {
      setSelectMonth(selectMonth + 1);
    }

    // 日付の更新
    // 当月の場合、今日の日付をセットする
    if (selectMonth + 1 == currentDate.getMonth()) {
      setSelectDate(currentDate.getDate());
    } else {
      setSelectDate(1);
    }
  };

  /**
   * カレンダー年月選択
   */
  const selectCalender = (date: Date) => {
    setSelectYear(date.getFullYear());
    setSelectMonth(date.getMonth());
    setSelectDate(date.getDate());
    setDispDate(date);
  };

  /**
   * カレンダー年月選択Picker表示
   */
  const onClickCalenderIcon = () => {
    setIsOpenCalender(!isOpenCalender);
  };


  /** 
   * 今日のカレンダーを配列で返す
   * 0: 日付(DD)
   * 1: weekday(平日) or sunday(日曜日) or saturday(土曜日)
   * 2: 日付(YYYY-MM-DD)
   * 3: 配列(作業日誌)
   * 4: 配列(診断結果)
   * 5: 配列(イベント)
   */
  const setDays = (date: Date) => {
    let result = [];  // 最後に返す配列
    let count = 1;  // 一日から月末まで移動する変数
    date.setDate(1);  // その月の一日に移動
    let day = date.getDay();  // 一日の曜日を取得
    let daysOfMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if ((date.getFullYear() % 400 == 0) || (date.getFullYear() % 100 != 0 && date.getFullYear() % 4 == 0)) {
      daysOfMonth[1] = 29;  // うるう年
    }

    // 一日から月末まで入力
    while (count <= daysOfMonth[date.getMonth()]) {
      let week = [
        [0, "weekday", "", new Array(), new Array(), new Array()],
        [0, "weekday", "", new Array(), new Array(), new Array()],
        [0, "weekday", "", new Array(), new Array(), new Array()],
        [0, "weekday", "", new Array(), new Array(), new Array()],
        [0, "weekday", "", new Array(), new Array(), new Array()],
        [0, "weekday", "", new Array(), new Array(), new Array()],
        [0, "weekday", "", new Array(), new Array(), new Array()]
      ];
      while (day != 7) {
        if (day == 0) {
          week[day][1] = "sunday";
        } else if (day == 6) {
          week[day][1] = "saturday";
        }
        const new_date = new Date(date.getFullYear(), date.getMonth(), count);
        week[day][2] = formatDate(new_date);
        week[day++][0] = count++;

        if (count > daysOfMonth[date.getMonth()]) {
          break;
        }
      }
      result.push(week);
      day = 0;
    }

    // 最初の週の空きに前月の日付を入力
    day = date.getDay() - 1;
    let prevMonth = daysOfMonth[(date.getMonth() + 11) % 12];
    for (let i = day; i >= 0; i--) {
      result[0][i][0] = prevMonth--;
      result[0][i][1] = "otherday";
    }

    // 最後の週の空きに次月の日付を入力
    day = (date.getDay() + daysOfMonth[date.getMonth()]) % 7;
    if (day != 0) {
      let num = 1;
      while (day < 7) {
        result[result.length - 1][day][1] = "otherday";
        result[result.length - 1][day++][0] = num++;
      }
    }

    return result;
  };

  /** 
   * 曜日毎レイアウト設定
   */
  const getStyle = (day: any) => {
    switch (day) {
      case "sunday":
        return { color: "#FF9292" };
      case "saturday":
        return { color: "#447CC4" };
      case "otherday":
        return { color: "#7F7F7F" };
      default:
        return { color: "#7F7F7F" };
    }
  };

  /** 
   * カテゴリーレイアウト設定
   */
  const getCategoryStyle = (category: any) => {
    const base = {
      borderTopWidth: 1,
      borderBottomWidth: 1,
      BorderStyle: "solid",
    };
    switch (category) {
      case Category.FLOWERING:
        return { ...base, backgroundColor: "#f08080", borderColor: "#f08080" };
      case Category.PESTICIDE_SPRAYNING:
        return { ...base, backgroundColor: "#F2A46E", borderColor: "#F2A46E" };
      case Category.FRUITS_THINNING:
        return { ...base, backgroundColor: "#5b9bd5", borderColor: "#5b9bd5" };
      case Category.MATING:
        return { ...base, backgroundColor: "#ffd700", borderColor: "#ffd700" };
      case Category.HARVEST:
        return { ...base, backgroundColor: "#bb6edb", borderColor: "#bb6edb" };
      case Category.OTHERS:
        return { ...base, backgroundColor: "#a6a6a6", borderColor: "#a6a6a6" };
      default:
        return { ...base, backgroundColor: "#70ad47", borderColor: "#70ad47" };
    }
  };
  const getEventCategoryStyle = (category: any) => {
    const base = {
      backgroundColor: "#ffffff",
      borderTopWidth: 1,
      borderBottomWidth: 1,
      BorderStyle: "solid",
    };
    switch (category) {
      case EventCategories.FLOWERING:
        return { ...base, color: "#f08080", borderColor: "#f08080" };
      case EventCategories.PESTICIDE_SPRAYNING:
        return { ...base, color: "#F2A46E", borderColor: "#F2A46E" };
      case EventCategories.FRUITS_THINNING:
        return { ...base, color: "#5b9bd5", borderColor: "#5b9bd5" };
      case EventCategories.MATING:
        return { ...base, color: "#ffd700", borderColor: "#ffd700" };
      case EventCategories.HARVEST:
        return { ...base, color: "#bb6edb", borderColor: "#bb6edb" };
      case EventCategories.OTHERS:
        return { ...base, color: "#a6a6a6", borderColor: "#a6a6a6" };
      default:
        return { ...base, color: "#70ad47", borderColor: "#70ad47" };
    }
  };

  /** 
   * 作業日誌画面レイアウト 
   */
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: "#E2F0D9",
    },
    headerContainer: {
      flexDirection: 'row',
      zIndex: pickerZIndex
    },
    calendarNavContainer: {
      flexDirection: "row",
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 100
    },
    calendarContainer: {
      borderTopWidth: 2,
      BorderStyle: "solid",
      borderColor: "#000000",
      marginBottom: 5,
    },
    diaryContainer: {
      flex: 1
    },
  });

  /** 
   * ヘッダーレイアウト 
   */
  const headerStyles = StyleSheet.create({
    reportLink: {
      marginTop: 5,
      marginLeft: 10,
      marginRight: "auto",
      alignItems: 'center',
    },
    reportText: {
      width: 40,
      fontSize: 10,
      color: "#78ac44",
    },
    dropDownPicker: {
      width: "50%",
      marginTop: 5,
      marginLeft: "auto",
      marginRight: 0,
    },
    dropDownPickerItems: {
      backgroundColor: "#ffffff",
      flexDirection: 'row',
      borderWidth: 2,
      BorderStyle: "solid",
      borderColor: "#78ac44",
    },
    dropDownPickerLabel: {
      textAlign: 'center',
    },
    dropDownPickerSelectedLabel: {
      color: '#78ac44',
    },
    dropDownContainer: {
      borderWidth: 2,
      BorderStyle: "solid",
      borderColor: "#78ac44",
    },
    dropDownText: {
      fontSize: 18,
    },
  });

  /** 
   * 月選択ナビゲーションレイアウト 
   */
  const selectCalenderStyles = StyleSheet.create({
    navButton: {
      fontSize: 24,
    },
    navText: {
      fontSize: 18,
      marginHorizontal: 10,
      width: 150,
      fontWeight: "bold",
      textAlign: "center",
    },
    datePicker: {
      position: "absolute",
      right: 0,
      top: 30,
    },
    filterView: {
      position: "absolute",
      right: 35,
    },
    datePickericonView: {
      position: "absolute",
      right: 5,
    }
  });

  /** 
   * カレンダー部レイアウト 
   */
  const calendarStyles = StyleSheet.create({
    calendarWeek: {
      height: (dispCalenderNum + 2) * 15,
      borderBottomWidth: 2,
      borderLeftWidth: 2,
      BorderStyle: "solid",
      borderColor: "#000000",
      flexDirection: "row"
    },
    daysFont: {
      fontSize: 10,
      marginLeft: 3,
    },
    calendarCell: {
      flex: 1 / 7,
      borderRightWidth: 2,
      BorderStyle: "solid",
      borderColor: "#000000",
    },
    calenderItem: {
      fontSize: 9,
      color: "#ffffff",
    },
    calenderOtherItem: {
      fontSize: 9,
      color: "#000000",
    }
  });

  /** 
   * 日誌操作部レイアウト 
   */
  const diaryStyles = StyleSheet.create({
    // 日付
    datePart: {
      backgroundColor: "#78ac44",
      borderColor: "#000000",
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    selectedDate: {
      color: "#ffffff",
      fontSize: 20,
    },
    // 作業登録リンク
    addEventPart: {
      backgroundColor: "#FFFFFF",
      borderColor: "#000000",
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 5,
    },
    addLink: {
      width: "90%",
      textAlign: "center",
      fontSize: 20,
      textDecorationLine: "underline",
    },
    // AI診断リンク
    linkPart: {
      backgroundColor: "#C0DCAC",
      borderColor: "#000000",
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 5,
    },
    aiLink: {
      width: "90%",
      textAlign: "center",
      fontSize: 20,
    },
    // 作業日誌リスト
    categoryView: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 5,
    },
    categoryText: {
      fontSize: 18,
      fontWeight: "bold",
    },
    authorText: {
      marginLeft: 10,
      fontSize: 18,
    },
    diaryInfoIconView: {
      position: "absolute",
      right: prodGroupId ? 5 : 50,
    },
    diaryPencilIconView: {
      position: "absolute",
      right: 5,
    }
  });

  const iconStyles = StyleSheet.create({
    homeImage: {
      position: "absolute",
      top: 50,
      left: 5,
      zIndex: 10,
      borderRadius: 25,
      width: 35,
      height: 35,
      backgroundColor: "#FF5050",
    },
    homeIcon: {
      width: 25,
      height: 25,
      top: 5,
      left: 5
    },
    reportIcon: {
      width: 40,
      height: 40,
    },
    diaryPlusIcon: {
      width: 25,
      height: 25,
    },
    diaryArrowIcon: {
      width: 25,
      height: 25,
    },
    diaryInfoIcon: {
      width: 25,
      height: 25,
    },
    diaryPencilIcon: {
      width: 25,
      height: 25,
    }
  });

  const dialogStyles = StyleSheet.create({
    dialogContainer: {
      position: "absolute",
      width: "100%",
      height: "100%",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(150,150,150,0.6)",
    },
    dialogIcon: {
      width: 30,
      height: 30,
    },
    categoryArea: {
      alignItems: "center",
      marginTop: 30,
    },
    categoryText: {
      fontSize: 24,
    },
    dialogBox: {
      width: "100%",
      height: 580,
      backgroundColor: "#FDF1E9",
      borderBottomColor: "#2C6393",
      borderBottomWidth: 2,
      borderTopColor: "#2C6393",
      borderTopWidth: 2,
      borderStyle: "solid"
    },
    dialogText: {
      fontSize: 18,
      width: "100%",
      backgroundColor: "#ffffff",
      textAlign: "center",
      borderBottomColor: "#EB6E19",
      borderBottomWidth: 2,
      borderTopColor: "#EB6E19",
      borderTopWidth: 2,
      borderStyle: "solid",
      marginBottom: 20,
    },
    dialogBottomBox: {
      flex: 0.2,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    memoArea: {
      fontSize: 18,
      width: "100%",
      height: 200,
      backgroundColor: "#ffffff",
      textAlign: "center",
      borderBottomColor: "#EB6E19",
      borderBottomWidth: 2,
      borderTopColor: "#EB6E19",
      borderTopWidth: 2,
      borderStyle: "solid",
      marginBottom: 20,
    }
  });

  // 作業日誌詳細ダイアログ表示
  const DiaryDetail = () => {
    if (selectDiary) {
      let calcTime = Number(selectDiary?.workingTime);
      let hour = Math.floor(calcTime / 60);
      let min = calcTime % 60;
      const tmpVarieties = [];
      if (selectDiary.varieties) {
        for (const variety of selectDiary.varieties) {
          if (variety) {
            tmpVarieties.push(CmnUtil.varietiesCnv(variety));
          }
        }
      }

      return (
        <Modal
          transparent={true}
          visible={ismodal}
          onRequestClose={() => {
            setIsModal(!ismodal);
          }}
        >
          <View style={dialogStyles.dialogContainer}>
            <View style={dialogStyles.dialogBox}>
              <View style={dialogStyles.dialogBottomBox}>
                {!prodGroupId ?
                  <TouchableOpacity onPress={() => { goDiayEdit("Edit", selectDiary, selectKind); setIsModal(!ismodal); }} >
                    <Image style={dialogStyles.dialogIcon} source={IconImages.icPencilBlack} />
                  </TouchableOpacity>
                  : <></>
                }
                <TouchableOpacity onPress={() => setIsModal(!ismodal)} >
                  <Image style={dialogStyles.dialogIcon} source={IconImages.icCloseBlack} />
                </TouchableOpacity>
              </View>

              <View style={dialogStyles.categoryArea}>
                {selectKind == "dailyLog" ?
                  <Text style={dialogStyles.categoryText}>{CmnUtil.getCategoryName(selectDiary?.category, selectDiary?.categoryOptional)}</Text>
                  :
                  <Text style={dialogStyles.categoryText}>{CmnUtil.getCategoryName(selectDiary?.eventCategory, selectDiary?.eventCategoryOptional)}</Text>
                }
              </View>
              <View>
                <Text style={dialogStyles.dialogText}>{selectDiary?.farm.name}</Text>
                {selectKind == "dailyLog" ?
                  <>
                    <Text style={dialogStyles.dialogText}>{formatDateAndWeek(formatToDate(selectDiary?.date))}</Text>
                    <Text style={dialogStyles.dialogText}>{hour}時間 {min} 分</Text>
                  </>
                  :
                  <>
                    <Text style={dialogStyles.dialogText}>{formatDateAndWeek(formatToDate(selectDiary?.startDate))}</Text>
                    <Text style={dialogStyles.dialogText}>{formatDateAndWeek(formatToDate(selectDiary?.finishDate))}</Text>
                  </>
                }
                {tmpVarieties.length > 0 && <Text style={dialogStyles.dialogText}>{tmpVarieties.join("・")}</Text>}
                <Text style={dialogStyles.dialogText}>{selectDiary?.authorName}</Text>
              </View>
              <View>
                <Text>メモ</Text>
                <View>
                  <Text style={dialogStyles.memoArea}>{selectKind == "dailyLog" ? selectDiary?.workMemo : selectDiary?.memo}</Text>
                </View>
              </View>
            </View>
          </View>
        </Modal>
      );
    } else {
      return (
        <View></View>
      );
    }
  };

  /**
   * カレンダー
   */
  const CalenderCell = ({ diaryItem }: { diaryItem: any; }) => {

    /**
     * 選択日付レイアウト
     */
    const selectCell = (item: any) => {
      let style = { backgroundColor: "#ffffff" };
      if (item[2] && item[2] == formatDate(dispDate)) {
        style = { backgroundColor: "#fffacd" };
      }
      return style;
    };

    const CellItem = ({ item }: { item: any; }) => {
      let itemTotal = item[5].length + (item[4].length > 0 ? 1 : 0) + item[3].length;
      let itemCount = 0;

      return (
        <View>
          <Text style={[calendarStyles.daysFont, getStyle(item[1])]}>{item[0]}</Text>
          {/* イベント */}
          {item[5].map((event: CultivationEvent, index: any) => {
            if (itemCount < dispCalenderNum) {
              itemCount++;
              return (
                <Text key={index} style={[calendarStyles.calenderItem, getEventCategoryStyle(event.eventCategory)]}>
                  {CmnUtil.getCategoryName(event.eventCategory, event.eventCategoryOptional, true)}
                </Text>
              );
            }
          })}

          {/* 診断結果 */}
          {item[4].map((diagnosis: DayDiagnosis, index: any) => {
            if (index == 0 && itemCount < dispCalenderNum) {
              itemCount++;
              return (
                <Text key={index} style={[getCategoryStyle('diagnosis'), calendarStyles.calenderItem]}>診断</Text>
              );
            }
          })}

          {/* 作業記録 */}
          {item[3].map((diary: DailyLog, index: any) => {
            if (itemCount < dispCalenderNum) {
              itemCount++;
              return (
                <Text key={index} style={[getCategoryStyle(diary.category), calendarStyles.calenderItem]}>
                  {CmnUtil.getCategoryName(diary.category, diary.categoryOptional, true)}
                </Text>
              );
            }
          })}

          {/* その他 */}
          {itemTotal > dispCalenderNum &&
            <Text style={[calendarStyles.calenderOtherItem]}>
              ほか{itemTotal - dispCalenderNum}件
            </Text>
          }
        </View>
      );
    };

    return (
      diaryItem[1] !== 'otherday' ?
        <TouchableOpacity style={[calendarStyles.calendarCell, selectCell(diaryItem)]} onPress={() => dispDiaryList(diaryItem)}>
          <CellItem item={diaryItem} />
        </TouchableOpacity>
        :
        <View style={[calendarStyles.calendarCell, selectCell(diaryItem)]}>
          <CellItem item={diaryItem} />
        </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <DiaryDetail />
      <FilterSelect
        display={conditionDisp}
        condition={condition}
        closeFunction={(data: any) => {
          setConditionDisp(!conditionDisp);
          setCondition(data);
        }}
      />
      {/* ヘッダー */}
      {coopGroupName ?
        <Header
          headerText={coopGroupName + 'さん'}
          headerTextColor={"#ffffff"}
          headerContainerColor={"#70AD47"} />
        : <></>}
      <View style={styles.headerContainer}>
        <TouchableOpacity style={headerStyles.reportLink} onPress={() => goReport()} >
          <Image style={iconStyles.reportIcon} source={IconImages.icReport} />
          <Text style={headerStyles.reportText}>レポート</Text>
        </TouchableOpacity>
        <View style={headerStyles.dropDownPicker}>
          <DropDownPicker
            open={open}
            value={pickerValue}
            items={pickerItems}
            setOpen={setOpen}
            setValue={setPickerValue}
            setItems={setPickerItems}
            onChangeValue={changeFarm}
            showTickIcon={false}
            placeholder={pickerValue}
            style={headerStyles.dropDownPickerItems}
            textStyle={headerStyles.dropDownText}
            dropDownContainerStyle={headerStyles.dropDownContainer}
            labelStyle={headerStyles.dropDownPickerLabel}
            selectedItemLabelStyle={headerStyles.dropDownPickerSelectedLabel}
            onOpen={() => { setPickerZIndex(1000); }}
            onClose={() => { setPickerZIndex(0); }}
          />
        </View>
      </View>

      {/* 月選択ナビゲーション */}
      {farmList.length > 0 &&
        <View style={styles.calendarNavContainer}>
          <TouchableOpacity onPress={prevMonth}>
            <Text style={selectCalenderStyles.navButton}>&lt;</Text>
          </TouchableOpacity>
          <Text style={selectCalenderStyles.navText}>{selectYear}年{selectMonth + 1}月</Text>
          <TouchableOpacity onPress={nextMonth}>
            <Text style={selectCalenderStyles.navButton}>&gt;</Text>
          </TouchableOpacity>
          {condition.length > 0 && <TouchableOpacity onPress={() => { setConditionDisp(!conditionDisp) }} style={selectCalenderStyles.filterView}>
            <MaterialCommunityIcons
              name={"filter-outline"}
              size={35}
              color={isNarrowed() ? "#ff0000" : "#4b4b4b"}
            />
          </TouchableOpacity>}
          <TouchableOpacity onPress={onClickCalenderIcon} style={selectCalenderStyles.datePickericonView}>
            <Image style={iconStyles.diaryArrowIcon} source={IconImages.icSearch} />
          </TouchableOpacity>
          {isOpenCalender && (
            <View style={selectCalenderStyles.datePicker}>
              <DatePicker
                locale={ja}
                selected={dispDate}
                onChange={(date: Date) => { selectCalender(date); onClickCalenderIcon(); }}
                showMonthYearPicker
                inline
              />
            </View>
          )}
        </View>
      }

      {/* カレンダー部 */}
      {farmList.length > 0 ?
        <View style={styles.calendarContainer}>
          {diaryList.map((diaryItem, index) => {
            return (
              <View style={calendarStyles.calendarWeek} key={index}>
                <CalenderCell diaryItem={diaryItem[0]} />
                <CalenderCell diaryItem={diaryItem[1]} />
                <CalenderCell diaryItem={diaryItem[2]} />
                <CalenderCell diaryItem={diaryItem[3]} />
                <CalenderCell diaryItem={diaryItem[4]} />
                <CalenderCell diaryItem={diaryItem[5]} />
                <CalenderCell diaryItem={diaryItem[6]} />
              </View>
            );
          })}
        </View> :
        <View style={styles.calendarContainer}>
          <Text style={{
            fontSize: 25,
            fontWeight: "bold",
            textAlign: "center",
            marginTop: 20,
          }}>圃場が登録されていません。{"\n"}「設定」→「圃場管理」から圃場の登録を行ってください。</Text>
        </View>
      }

      {/* 日誌操作部 */}
      {farmList.length > 0 &&
        <View style={styles.diaryContainer}>
          <View style={diaryStyles.datePart}>
            <Text style={diaryStyles.selectedDate}>{formatDateAndWeek(dispDate)}</Text>
          </View>
          {!prodGroupId ?
            <TouchableOpacity style={diaryStyles.addEventPart} onPress={() => goDiayEdit("Create")}>
              <Text style={diaryStyles.addLink}>作業やイベントを追加する</Text>
              <Image style={iconStyles.diaryPlusIcon} source={IconImages.icPlus} />
            </TouchableOpacity>
            : <></>}
          <ScrollView>
            <View>
              {selectDayEvent.map((item, index) => {
                return (
                  <TouchableOpacity
                    onPress={() => dispDiayDetail(item, "event")}
                    key={index}
                    style={[getEventCategoryStyle(item.eventCategory), diaryStyles.categoryView]}>
                    <Text style={diaryStyles.categoryText}>
                      {CmnUtil.getCategoryName(item.eventCategory, item.eventCategoryOptional)}
                    </Text>
                    <Text style={diaryStyles.authorText}>
                      {"(" + item.authorName + ")"}
                    </Text>
                    <TouchableOpacity
                      onPress={() => dispDiayDetail(item, "event")}
                      style={diaryStyles.diaryInfoIconView}>
                      <Image style={iconStyles.diaryInfoIcon} source={IconImages.icInfoBlack} />
                    </TouchableOpacity>
                    {!prodGroupId ?
                      <TouchableOpacity
                        onPress={() => goDiayEdit("Edit", item, "event")}
                        style={diaryStyles.diaryPencilIconView}>
                        <Image style={iconStyles.diaryPencilIcon} source={IconImages.icPencilBlack} />
                      </TouchableOpacity>
                      : <></>}
                  </TouchableOpacity>
                );
              })}
            </View>
            {selectDayDignosis.length !== 0 &&
              <TouchableOpacity style={diaryStyles.linkPart} onPress={() => goDiagnosisPhotoList()}>
                <Text style={diaryStyles.aiLink}>AI診断結果と撮影画像を見る</Text>
                <TouchableOpacity onPress={() => goDiagnosisPhotoList()}>
                  <Image style={iconStyles.diaryArrowIcon} source={IconImages.icRightArrow} />
                </TouchableOpacity>
              </TouchableOpacity>}
            <View>
              {selectDiaryList.map((item, index) => {
                return (
                  <TouchableOpacity
                    onPress={() => dispDiayDetail(item, "dailyLog")}
                    key={index}
                    style={[getCategoryStyle(item.category), diaryStyles.categoryView]}>
                    <Text style={diaryStyles.categoryText}>
                      {CmnUtil.getCategoryName(item.category, item.categoryOptional)}
                    </Text>
                    <Text style={diaryStyles.authorText}>
                      {"(" + item.authorName + ")"}
                    </Text>
                    <TouchableOpacity
                      onPress={() => dispDiayDetail(item, "dailyLog")}
                      style={diaryStyles.diaryInfoIconView}>
                      <Image style={iconStyles.diaryInfoIcon} source={IconImages.icInfoBlack} />
                    </TouchableOpacity>
                    {!prodGroupId ?
                      <TouchableOpacity
                        onPress={() => goDiayEdit("Edit", item, "dailyLog")}
                        style={diaryStyles.diaryPencilIconView}>
                        <Image style={iconStyles.diaryPencilIcon} source={IconImages.icPencilBlack} />
                      </TouchableOpacity>
                      : <></>}
                  </TouchableOpacity>
                );
              })}
            </View>
          </ScrollView>
          {/* {prodGroupId ?
          <TouchableOpacity
            style={iconStyles.homeImage}
            onPress={() => onHome()} >
            <Image
              resizeMode="contain"
              style={iconStyles.homeIcon}
              source={{ uri: IconImages.icHome }} />
          </TouchableOpacity>
          : <></>} */}
        </View>
      }
    </SafeAreaView>
  );
};

// 絞り込みダイアログ表示
const FilterSelect = ({
  display,
  condition,
  closeFunction
}: {
  display: boolean,
  condition: any,
  closeFunction: Function
}) => {

  const [tmpCondition, setTmpCondition] = useState<{ checked: boolean, value: Varieties }[]>([]);

  useEffect(() => {
    setTmpCondition([...condition]);
  }, [condition]);

  const onClickCondition = (variety: any) => {
    const tmpVarieties: any = [];
    for (const tmp of tmpCondition) {
      if (tmp.value == variety) {
        tmpVarieties.push({ ...tmp, checked: !tmp.checked });
      } else {
        tmpVarieties.push({ ...tmp });
      }
    }
    setTmpCondition(tmpVarieties);
  };

  const dialogStyles = StyleSheet.create({
    dialogContainer: {
      position: "absolute",
      width: "100%",
      height: "100%",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(150,150,150,0.6)",
    },
    dialogBox: {
      width: "95%",
      borderWidth: 1,
      borderColor: "#cccccc",
      borderStyle: "solid",
      backgroundColor: "#ffffff",
    },
    dialogBottomBox: {
      height: 50,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: "#78ac44",
      padding: 20,
    },
    dialogIcon: {
      width: 30,
      height: 30,
    },
    headerText: {
      fontSize: 24,
      color: "#ffffff",
    },
  });

  return (
    <Modal
      transparent={true}
      visible={display}
    >
      <View style={dialogStyles.dialogContainer}>
        <View style={dialogStyles.dialogBox}>
          <View style={dialogStyles.dialogBottomBox}>
            <Text>　</Text>
            <Text style={dialogStyles.headerText}>品種絞り込み</Text>
            <TouchableOpacity onPress={() => closeFunction([...tmpCondition])} >
              <Image style={dialogStyles.dialogIcon} source={IconImages.icClose} />
            </TouchableOpacity>
          </View>

          <View style={{ paddingVertical: 30, paddingHorizontal: 10 }}>
            {tmpCondition.map((data: any, index: any) => (
              <TouchableOpacity
                key={index}
                onPress={() => {
                  onClickCondition(data.value);
                }}
                style={{ flexDirection: "row", alignItems: "center", height: 50, paddingLeft: 30, borderBottomWidth: 1, borderColor: "#78ac44", borderStyle: "solid" }}>
                <Checkbox
                  status={data.checked ? "checked" : "unchecked"}
                  color="#507E32"
                />
                <Text style={{ fontSize: 24, color: "#78ac44", paddingLeft: 10 }}>{CmnUtil.varietiesCnv(data.value)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default DiaryTop;